import React, { useCallback, useContext, useEffect, useState } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import {
	useAnalyticsEvents,
	AnalyticsListener,
	type UIAnalyticsEvent,
} from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';

import type { DialogsStateContainer } from '@confluence/dialogs';
import {
	ExperienceTrackerContext,
	SUBMIT_GUEST_MENTIONS_UPGRADE_FLOW_EXPERIENCE,
} from '@confluence/experience-tracker';
import type { FlagsStateContainer } from '@confluence/flags';
import { useIsGuest } from '@confluence/session-data';

import { useGuestRequestToUpgradeStatus } from '../../entry-points/useGuestRequestToUpgradeStatus';

import { GuestRequestUpgradeButton } from './GuestRequestUpgradeButton';
import { GuestRequestToUpgradeLearnMoreDialogModal } from './GuestRequestToUpgradeLearnMoreModal';
import { useSubmitGuestUpgradeRequest } from './useSubmitGuestUpgradeRequest';
import { UpgradeStatus } from './types';

const TRACK_EVENT = 'sendTrackEvent';
const ANALYTICS_SOURCE = 'guestAttemptedMention';

export const i18n = defineMessages({
	mentionFlagTitle: {
		id: 'external-collab-ui.guest-mention.restricted.flag.title',
		defaultMessage: 'You can’t mention people as a guest',
		description:
			'Title text for the flag explaining that guests cannot mention users when they try to',
	},
	mentionFlagDescription: {
		id: 'external-collab-ui.guest-mention.restricted.flag.description',
		defaultMessage: 'To mention people, you need full access to Confluence.',
		description:
			'Body text for the flag explaining that guests cannot mention users when they try to',
	},
	learnMoreButton: {
		id: 'external-collab-ui.guest-mention.learn-more-button',
		defaultMessage: 'Learn more',
		description: 'Button text for guests to learn more about upgrading to a full Confluence user',
	},

	flagSuccessTitle: {
		id: 'external-collab-ui.guest-request-to-upgrade.flag-success-title',
		defaultMessage: 'Licensed user request sent!',
		description:
			'Flag notification title informing the guest that their request to be upgraded to a licensed user was successfully submitted',
	},
	flagSuccessDescription: {
		id: 'external-collab-ui.guest-request-to-upgrade.flag-success-description',
		defaultMessage: 'If approved, you’ll get a confirmation message from admin',
		description: 'Flag notification description informing guest that request was submitted',
	},

	flagErrorTitle: {
		id: 'external-collab-ui.guest-request-to-upgrade.flag-error-title',
		defaultMessage: 'Request not sent',
		description: 'Flag notification title informing guest that request encountered an error',
	},
	flagErrorDescription: {
		id: 'external-collab-ui.guest-request-to-upgrade.flag-error-description',
		defaultMessage: 'Refresh the page to resubmit your request, or try again later.',
		description: 'Flag notification description informing guest that request encountered an error',
	},
});

const requestButtonContainerStyles = xcss({
	marginLeft: 'space.negative.150',
	marginTop: 'space.negative.025',
});

const learnMoreButtonContainerStyles = xcss({
	marginTop: 'space.negative.025',
});

type MentionsNotificationsWrapperProps = {
	children: React.ReactNode;
	dialogs: DialogsStateContainer;
	flags: FlagsStateContainer;
	suppressListener?: boolean;
};

export const GuestMentionsNotificationsWrapper = ({
	children,
	dialogs,
	flags,
	suppressListener = false,
}: MentionsNotificationsWrapperProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const [attemptedMention, setAttemptedMention] = useState<boolean>(false);
	const [flagNeedsUpdate, setFlagNeedsUpdate] = useState<boolean>(false);
	const isGuest = useIsGuest();

	const { formatMessage } = useIntl();

	const shouldDisplayGuestUpgradeFlow = isGuest && attemptedMention;

	const handleFlagClose = useCallback(() => {
		void flags.hideAllFlags();
		setAttemptedMention(false);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'flag',
				actionSubjectId: 'guestMentionRequestAccess',
				source: ANALYTICS_SOURCE,
				attributes: {
					touchpointId: ANALYTICS_SOURCE,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, flags]);

	const showErrorFlag = useCallback(
		(submissionError: Error) => {
			void flags.showErrorFlag({
				title: <FormattedMessage {...i18n.flagErrorTitle} />,
				description: <FormattedMessage {...i18n.flagErrorDescription} />,
			});
			experienceTracker.fail({
				name: SUBMIT_GUEST_MENTIONS_UPGRADE_FLOW_EXPERIENCE,
				error: submissionError,
			});
		},
		[experienceTracker, flags],
	);

	const onGuestUpgradeRequestSubmitted = useCallback(() => {
		handleFlagClose();
		void flags.showSuccessFlag({
			title: <FormattedMessage {...i18n.flagSuccessTitle} />,
			description: <FormattedMessage {...i18n.flagSuccessDescription} />,
		});
		experienceTracker.succeed({
			name: SUBMIT_GUEST_MENTIONS_UPGRADE_FLOW_EXPERIENCE,
		});
	}, [experienceTracker, flags, handleFlagClose]);

	const handleOnSubmit = (error?: Error) => {
		createAnalyticsEvent({
			type: TRACK_EVENT,
			data: {
				action: !!error ? 'failed' : 'requested',
				actionSubject: 'productAccess',
				source: ANALYTICS_SOURCE,
				attributes: {
					touchpointId: ANALYTICS_SOURCE,
				},
			},
		}).fire();

		if (!error) {
			onGuestUpgradeRequestSubmitted();
		} else {
			showErrorFlag(error);
		}
	};

	const [
		submitGuestUpgradeRequest,
		{ loading: guestUpgradeRequestIsLoading, isSubmitted: guestUpgradeSubmitted },
	] = useSubmitGuestUpgradeRequest({ onCompleted: handleOnSubmit });

	const { upgradeStatus, loading: permissionLoading } = useGuestRequestToUpgradeStatus(
		!shouldDisplayGuestUpgradeFlow,
	);

	const permissionDenied = upgradeStatus === UpgradeStatus.NOT_ELIGIBLE_TO_UPGRADE_AT_ALL;

	const requestPending =
		guestUpgradeSubmitted || upgradeStatus === UpgradeStatus.AN_UPGRADE_REQUEST_IS_PENDING;

	useEffect(() => {
		if (permissionLoading || !shouldDisplayGuestUpgradeFlow) {
			return;
		}

		const submitGuestUpgradeRequestWithAnalytics = (
			_: React.MouseEvent<HTMLElement, MouseEvent>,
			analyticsEvent: UIAnalyticsEvent,
		) => {
			setFlagNeedsUpdate(true);
			const analyticsAttributes = {
				...analyticsEvent.payload.attributes,
				touchpointId: ANALYTICS_SOURCE,
			};
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					...analyticsEvent.payload,
					actionSubjectId: 'submitGuestUpgradeRequest',
					source: 'guestRequestAccess',
					attributes: analyticsAttributes,
				},
			}).fire();
			experienceTracker.start({
				name: SUBMIT_GUEST_MENTIONS_UPGRADE_FLOW_EXPERIENCE,
			});
			void submitGuestUpgradeRequest();
		};

		const handleOnSubmit = (error?: Error) => {
			createAnalyticsEvent({
				type: TRACK_EVENT,
				data: {
					action: !!error ? 'failed' : 'requested',
					actionSubject: 'productAccess',
					source: ANALYTICS_SOURCE,
					attributes: {
						touchpointId: ANALYTICS_SOURCE,
					},
				},
			}).fire();

			if (!error) {
				onGuestUpgradeRequestSubmitted();
			} else {
				showErrorFlag(error);
			}
		};

		const openLearnMoreModal = (
			_: React.MouseEvent<HTMLElement, MouseEvent>,
			analyticsEvent: UIAnalyticsEvent,
		) => {
			handleFlagClose();
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					...analyticsEvent.payload,
					actionSubjectId: 'guestUpgradeRequestLearnMore',
					source: 'guestRequestAccess',
					attributes: {
						requested: requestPending,
					},
				},
			}).fire();
			dialogs.showModal(GuestRequestToUpgradeLearnMoreDialogModal, {
				onSubmit: handleOnSubmit,
				requestPending,
				analytics: {
					touchpointId: ANALYTICS_SOURCE,
				},
			});
		};

		const requestUpgradeFlagId = 'guest-request-upgrade-flag';

		if (attemptedMention && flags.state.flags.length === 0) {
			void flags.showCustomFlag({
				id: requestUpgradeFlagId,
				title: formatMessage(i18n.mentionFlagTitle),
				description: formatMessage(i18n.mentionFlagDescription),
				isAutoDismiss: false,
				onClose: handleFlagClose,
				actions: permissionDenied
					? []
					: [
							{
								content: (
									<Box xcss={requestButtonContainerStyles}>
										<GuestRequestUpgradeButton
											appearance="link"
											requestPending={requestPending}
											loading={guestUpgradeRequestIsLoading}
											onClick={submitGuestUpgradeRequestWithAnalytics}
										/>
									</Box>
								),
							},
							{
								content: (
									<Box xcss={learnMoreButtonContainerStyles}>
										<Button appearance="link" onClick={openLearnMoreModal}>
											<FormattedMessage {...i18n.learnMoreButton} />
										</Button>
									</Box>
								),
							},
						],
			});
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'shown',
					actionSubject: 'button',
					actionSubjectId: 'submitGuestUpgradeRequest',
					source: ANALYTICS_SOURCE,
					attributes: {
						touchpointId: ANALYTICS_SOURCE,
					},
				},
			}).fire();
		} else if (flagNeedsUpdate && guestUpgradeRequestIsLoading && flags.state.flags.length === 1) {
			void flags.updateFlag(requestUpgradeFlagId, {
				actions: permissionDenied
					? []
					: [
							{
								content: (
									<Box xcss={requestButtonContainerStyles}>
										<GuestRequestUpgradeButton
											appearance="link"
											requestPending={requestPending}
											loading={guestUpgradeRequestIsLoading}
											onClick={submitGuestUpgradeRequestWithAnalytics}
										/>
									</Box>
								),
							},
							{
								content: (
									<Box xcss={learnMoreButtonContainerStyles}>
										<Button appearance="link" onClick={openLearnMoreModal}>
											<FormattedMessage {...i18n.learnMoreButton} />
										</Button>
									</Box>
								),
							},
						],
			});
			setFlagNeedsUpdate(false);
		}
	}, [
		attemptedMention,
		createAnalyticsEvent,
		dialogs,
		experienceTracker,
		flags,
		flagNeedsUpdate,
		formatMessage,
		guestUpgradeRequestIsLoading,
		handleFlagClose,
		onGuestUpgradeRequestSubmitted,
		permissionDenied,
		permissionLoading,
		requestPending,
		setFlagNeedsUpdate,
		shouldDisplayGuestUpgradeFlow,
		showErrorFlag,
		submitGuestUpgradeRequest,
	]);

	const handleGuestMentionEvent = (event: UIAnalyticsEvent) => {
		const { actionSubject, actionSubjectId } = event.payload;
		if (actionSubject === 'typeAhead' && actionSubjectId === 'mentionTypeAhead') {
			setTimeout(() => {
				setAttemptedMention(true);
			}, 5);
		}
	};

	if (!isGuest || suppressListener) {
		return <>{children}</>;
	}

	return (
		<AnalyticsListener onEvent={handleGuestMentionEvent} channel={FabricChannel.editor}>
			{children}
		</AnalyticsListener>
	);
};
